// SessionWalkthrough — Static visual walkthrough of the session assessment flow
// Reuses Composer CSS classes for mock inputs; this file handles layout and walkthrough-specific styles

.walkthrough {
  max-width: 900px;
  margin: 0 auto;
  padding: 4rem 2rem;

  h2 {
    font-size: 2rem;
    font-weight: 300;
    margin: 0 0 3rem 0;
    text-align: center;
    color: var(--qs-color-text);
  }
}

.bare {
  max-width: none;
  padding: 0;
  margin: 0;

  .frames { gap: 2rem; }
  .frame { grid-template-columns: 1fr; gap: 1rem; }
}

.frames {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.frame {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  align-items: center;
}

.frameText {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.frameNumber {
  font-family: var(--qs-font-display);
  font-size: 2rem;
  font-weight: 300;
  color: var(--qs-color-accent);
  line-height: 1;
}

.frameTitle {
  font-size: 1.2rem;
  font-weight: 400;
  margin: 0;
  color: var(--qs-color-text);
}

.frameDescription {
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
  color: var(--qs-color-text-soft);
}

.mockPanel {
  // Replicate Composer's scoped variables so imported Composer classes resolve correctly.
  // --qs-* is always defined on :root (tokens.generated.scss), so no fallback is needed —
  // unlike Composer's own Narcis-pink fallbacks, which guard a name that isn't always set.
  --composer-accent: var(--qs-color-accent);
  --composer-accent-glow: var(--qs-color-accent-tint);
  --composer-text: var(--qs-color-text);
  --composer-text-muted: var(--qs-color-text-soft);
  --composer-surface: var(--qs-color-surface);
  --composer-border: var(--qs-color-line);

  background: var(--qs-color-surface);
  border: 1px solid var(--qs-color-line);
  border-radius: 0.75rem;
  padding: 1.5rem;
  pointer-events: none;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;

  // Override disabled opacity on decorative inputs — they're frozen for display, not "unavailable"
  input:disabled {
    opacity: 1;
  }
}

// Delivery frame — therapeutic text mock. Reading text is Jost 300 (decisions.md §6), the same
// as Paragraph's .content — decision 32: the walkthrough renders the real reading style, not a
// marketing approximation of it.
.deliveryText {
  font-family: var(--qs-font-text);
  font-weight: 300;
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--qs-color-text-soft);
  text-align: center;
  padding: 0.5rem 0;

  &::after {
    content: '';
    display: inline-block;
    width: 2px;
    height: 1.1em;
    background: var(--qs-color-accent);
    margin-left: 3px;
    vertical-align: text-bottom;
    animation: blink var(--qs-motion-dur-reveal) step-end infinite;
  }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

// Audio player mock
.audioMock {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--qs-color-surface);
  border: 1px solid var(--qs-color-line);
  border-radius: 2rem;
}

.playIcon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  color: var(--qs-color-accent);
}

.progressTrack {
  flex: 1;
  height: 4px;
  background: var(--qs-color-line);
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}

.progressFill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: var(--qs-color-accent);
  border-radius: 2px;
  width: 35%;
}

// Shuffle hint annotation
.shuffleHintWrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.shuffleHint {
  font-size: 0.75rem;
  color: var(--qs-color-text-muted);
  opacity: 0.7;
  text-align: center;
  margin-top: 0.5rem;
  font-style: italic;
  max-width: 200px;
  line-height: 1.4;
}

// Mock writing area
.writingMock {
  width: 100%;
  min-height: 80px;
  padding: 0.75rem;
  font-family: inherit;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--qs-color-text-soft);
  opacity: 0.6;
}

// Responsive
@media (max-width: 768px) {
  .walkthrough {
    padding: 3rem 1rem;
  }

  .frame {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .mockPanel {
    padding: 1.25rem;
  }
}
